export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const resolveImage = (url) =>
  url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

export const waLink = (number, msg) =>
  `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;

export const renderLinkedText = (text) => {
  if (!text || typeof text !== "string") return text;
  return text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!m) return part;
    const [, label, url] = m;
    const external = url.startsWith("http");
    return (
      <a
        key={i}
        href={url}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className="font-semibold text-inherit underline decoration-[#C5A059] decoration-2 underline-offset-4 transition-colors hover:text-[#C5A059]"
      >
        {label}
      </a>
    );
  });
};

export function deepMerge(base, override) {
  if (Array.isArray(base) || Array.isArray(override)) {
    return override !== undefined ? override : base;
  }
  if (typeof base === "object" && base !== null && typeof override === "object" && override !== null) {
    const out = { ...base };
    for (const k of Object.keys(override)) {
      out[k] = k in base ? deepMerge(base[k], override[k]) : override[k];
    }
    return out;
  }
  return override !== undefined ? override : base;
}

export const DEFAULT_CONTENT = {
  site: { name: "Tricity Family Concierge", cities: "Chandigarh · Mohali · Panchkula", tagline: "Parents. Property. Peace of mind." },
  contact: {
    phone_display: "+91 98912 43309",
    whatsapp_number: "919891243309",
    enquiry_email: "care@tricityfamilyconcierge.com",
  },
  hero: {
    badge: "Parent care & property management for NRI families",
    promise_line: "You live abroad. We stay close.",
    h1_line1: "NRI Parent Care & Property Management",
    h1_line2: "in Chandigarh, Mohali & Panchkula",
    lines: [
      { text: "You live abroad.", italic: false },
      { text: "We stay close.", italic: true },
      { text: "Parents. Property. Peace of mind.", italic: false },
    ],
    subtext:
      "Bi-weekly home visits, hospital appointment assistance and care for your family home — across Chandigarh, Mohali & Panchkula. With a photo report after every single visit.",
    image_url: "/images/hero-parents.jpg",
    stats: ["Bi-weekly home visits", "Report after every visit", "Background-verified team"],
    whatsapp_msg:
      "Sat Sri Akal! I'm an NRI and would like to know more about Tricity Family Concierge.",
  },
  marquee: {
    items: [
      "Chandigarh", "Mohali", "Panchkula", "ਸੱਭ ਤੋਂ ਵੱਡਾ ਧਨ — ਮਾਪੇ",
      "Bi-weekly visits", "Report after every visit", "Your parents, our family",
    ],
  },
  manifesto: {
    kicker: "Our Promise",
    title_line1: "The distance is hard.",
    title_line2: "The worry is harder.",
    intro:
      'Built by a Tricity family who understands both sides — the child refreshing WhatsApp at 2 a.m., and the parents who say "ਸਭ ਠੀਕ ਹੈ" when it isn\'t always.',
    chapters: [
      { num: "01", title: "They will never tell you.", body: "Parents hide the knee pain, the lonely afternoons, the leaking tap — because they don't want to worry you. Our bi-weekly visits notice what a phone call never can." },
      { num: "02", title: "Presence, not just payments.", body: "Money orders medicines. It cannot hold a hand in a hospital queue or share a cup of chai. We are not a medical service — we are the familiar face that gets them there, on time, never alone." },
      { num: "03", title: "Your home, watched over.", body: "An empty home ages quickly. We inspect, coordinate repairs and send you dated photo reports — so the home you grew up in stays the way you remember it." },
    ],
  },
  services: {
    kicker: "What We Do",
    title_line1: "Two promises.",
    title_line2: "One team.",
    intro: "Care for your parents, and care for the home they live in. Everything is documented and shared with you after every visit.",
    care: {
      title: "Parent Companionship & Home Visits",
      body: "Scheduled bi-weekly visits — chai, conversation, wellness check-ins, help with errands, and festival check-ins on your behalf. You receive a photo report every time.",
      cta_label: "Explore NRI Parent Care",
      cta_href: "/nri-parent-care",
      image: "https://images.unsplash.com/photo-1604881991575-dfb1003d8811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxjYXJlZ2l2ZXIlMjBob2xkaW5nJTIwaGFuZHN8ZW58MHx8fHwxNzg3OTQ5ODU1fDA&ixlib=rb-4.1.0&q=85",
    },
    appointments: {
      title: "Hospital & Appointment Assistance",
      body: "We book appointments, accompany your parents to the doctor, wait with them, and bring them home safely — then update you the same day.",
      honest_note: "We are not a medical or nursing provider. We make sure your parents reach the right care, on time, and never alone.",
    },
    property: {
      title: "NRI Property Management",
      body: "Professional on-ground property care for NRIs who own homes in the Tricity. We inspect vacant and tenanted homes, coordinate repairs and maintenance, assist with utilities and property-related tasks, support tenant coordination and provide dated photo reports so you know what is happening with your property.",
      cta_label: "Explore NRI Property Management",
      cta_href: "/nri-property-management",
      image: "https://images.unsplash.com/photo-1723110994499-df46435aa4b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBsdXh1cnklMjBob3VzZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODc5NDk4Njh8MA&ixlib=rb-4.1.0&q=85",
    },
    mini: [
      { title: "Emergency Coordination", body: "A local, reachable team when minutes matter — we get to them first and call you right after." },
      { title: "Bills & Errands", body: "Utility payments, paperwork runs and small daily tasks, handled quietly in the background." },
      { title: "Always Reachable", body: "One WhatsApp message reaches the person who will actually knock on your parents' door." },
    ],
  },
  how: {
    kicker: "How It Works",
    title_line1: "From first call to first visit,",
    title_line2: "in days — not months.",
    image: "https://images.unsplash.com/photo-1695787845331-4c06f963c37e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxjaGFuZGlnYXJoJTIwY2l0eXxlbnwwfHx8fDE3ODc5NDk4NTV8MA&ixlib=rb-4.1.0&q=85",
    cta_label: "Book Your Free Family Call",
    steps: [
      { num: "1", title: "Tell us about your family", body: "A free, no-pressure video or WhatsApp call. We listen — their routine, health appointments, the house, everything." },
      { num: "2", title: "We meet your parents", body: "Our Tricity team visits their home, builds trust over chai, and designs a care plan around their life — not a template." },
      { num: "3", title: "Visits begin", body: "Bi-weekly or weekly visits, appointment assistance and property checks — on a schedule your parents look forward to." },
      { num: "4", title: "You get every report", body: "Photos, videos and honest notes after every visit, straight to your WhatsApp. You'll feel the distance shrink." },
    ],
  },
  packages: {
    kicker: "Care Packages",
    title_line1: "Simple plans.",
    title_line2: "Serious peace of mind.",
    note: "Introductory monthly pricing. Every plan is finalised together after your free family consultation — no surprises, ever.",
    plans: [
      { id: "essential", name: "Essential Care", price: "₹4,999", period: "/month",
        tagline: "For parents who are independent, but shouldn't feel alone.",
        features: ["2 home visits every month (bi-weekly)", "Wellness & mood check-ins", "Help with errands and small daily needs", "Photo report after every visit", "WhatsApp updates to your family"],
        featured: false },
      { id: "complete", name: "Complete Care", price: "₹9,999", period: "/month",
        tagline: "Our most loved plan — visits, appointments and real companionship.",
        features: ["4 home visits every month (weekly)", "Hospital appointment scheduling & escort (2/month)", "Festival & birthday check-ins on your behalf", "Detailed photo + video report after every visit", "Priority family helpline, 7 days a week"],
        featured: true },
      { id: "care-property", name: "Care + Property", price: "₹14,999", period: "/month",
        tagline: "Everything in Complete Care, plus your home watched over.",
        features: ["Everything in Complete Care", "Monthly property inspection with photo report", "Repair & maintenance coordination", "Utility bill & local paperwork assistance", "Tenant / vacant-home check support"],
        featured: false },
    ],
  },
  trust: {
    kicker: "Why Families Trust Us",
    title_line1: "Don't take our word for it.",
    title_line2: "Take the report's.",
    intro: "Every visit ends with a dated photo report on your WhatsApp. Here is what one looks like.",
    report: {
      location: "Sharma Residence, Sector 35, Chandigarh",
      rows: [
        { label: "Mood & wellbeing", value: "Cheerful, slept well" },
        { label: "Home condition", value: "All good — geyser serviced" },
        { label: "Errands done", value: "Medicines picked up, electricity bill paid" },
        { label: "Next visit", value: "Tuesday, 11 a.m." },
      ],
      photos_note: "12 photos shared on WhatsApp",
    },
    badges: ["Background-verified team", "Photo report after every visit", "Honest written notes, every time", "Local Tricity team, always reachable"],
    commitments_title: "We're new — and that works in your favour",
    commitments: [
      { quote: "We are a new, founding family-run team — so every single parent we visit gets our personal attention, not a rotating roster of strangers.", name: "Our promise of personal care" },
      { quote: "If a visit report ever feels incomplete or late, your next visit is on us. Accountability is how a new business earns an NRI's trust.", name: "Our promise of accountability" },
      { quote: "We will never claim to be doctors or nurses. We show up, we accompany, we report honestly — and we tell you the truth even when it's uncomfortable.", name: "Our promise of honesty" },
    ],
  },
  enquiry: {
    kicker: "Begin Today",
    title_line1: "Your parents' next visit could be",
    title_line2: "this week.",
    intro: "Tell us a little about your family. We'll call you back within 24 hours — or skip the form and WhatsApp us right now.",
    whatsapp_msg: "Sat Sri Akal! I'd like to discuss a care plan for my parents in the Tricity.",
  },
  faq: {
    kicker: "Questions, Answered Honestly",
    title_line1: "Everything an NRI child",
    title_line2: "wants to ask.",
    items: [
      { q: "Do you provide medical or nursing services?", a: "No — and we believe in being honest about that. We are a companionship and assistance service. We schedule appointments, accompany your parents to the doctor, wait with them and bring them home safely. For medical care itself, we connect you with the right professionals." },
      { q: "Which areas do you serve?", a: "Chandigarh, Mohali and Panchkula (the Tricity), plus nearby areas around Chandigarh. If your parents live a little further out, message us — we often accommodate." },
      { q: "How do I know the visits actually happen?", a: "Every single visit ends with a dated photo report and honest written notes sent to your WhatsApp — mood, home condition, errands done, and the next visit date. For Complete Care and above, video updates are included." },
      { q: "Can I customise a package for my parents?", a: "Absolutely. The packages are starting points. After the free family consultation and our first meeting with your parents, we shape the plan around their real routine and needs." },
      { q: "How do we get started?", a: "Send an enquiry or WhatsApp us. We'll arrange a free video call with you, then visit your parents in person within days. There is no lock-in — plans run month to month." },
    ],
  },
  footer: {
    punjabi_tagline: "ਸੱਭ ਤੋਂ ਵੱਡਾ ਧਨ — ਮਾਪੇ",
    blurb: "Parent care and property management for NRI families, delivered with the warmth of home. Not a medical service — a family that shows up.",
  },
  property_page: {
    kicker: "For NRI homeowners",
    headline_line1: "NRI Property Management in",
    headline_line2: "Chandigarh, Mohali & Panchkula",
    subtext: "Your home in the Tricity, cared for like family. Monthly inspections with dated photo reports, repairs coordinated, tenant coordination, and bill and payment assistance — without you ever boarding a flight.",
    stats: ["Monthly photo reports", "Verified local team", "Vacant & tenanted homes"],
    whatsapp_msg: "Hi! I'm an NRI and I'd like a free consultation about managing my property in the Tricity.",
    risks_kicker: "The Real Risks",
    risks_title1: "An unwatched home in India",
    risks_title2: "doesn't stay safe by itself.",
    risks: [
      { title: "Encroachment & squatting", body: "An empty, unwatched house in Mohali or Chandigarh attracts trouble — from squatters to boundary disputes. Regular documented visits help identify issues early and maintain a clear property-condition record." },
      { title: "Silent damage", body: "A leaking pipe, a shorted wire, seepage in the monsoon — small problems become ₹1-lakh repairs when nobody is watching. Monthly inspections catch them early." },
      { title: "Tenant troubles", body: "Late rent, unverified tenants, agreements not renewed — we verify tenants, coordinate rent collection support, and keep paperwork current." },
      { title: "Bills & paperwork pile up", body: "Property tax, electricity, water, society maintenance — missed payments lead to penalties and disconnections. We track and assist with payments on your behalf." },
    ],
    audience: {
      kicker: "Who Is This Service For?",
      title1: "Made for NRIs who",
      title2: "own a home back here.",
      intro: "Whether you live in Toronto, London, Melbourne or anywhere else — if you own property in Chandigarh, Mohali or Panchkula, we keep it cared for while you're away. If your parents live in the home, pair this with our [parent care service](/p/nri-parent-care-mohali), or explore [everything we do for NRI families](/).",
      items: [
        { title: "Vacant properties", body: "Your house sits empty between your visits to India. We inspect it monthly, keep utilities current and send dated photo reports — so issues are identified early, never discovered too late." },
        { title: "Tenanted properties", body: "You have tenants but no local support. We coordinate tenant verification, rent follow-ups and agreement renewals on your behalf, with a written record of every visit." },
        { title: "Homes where parents live", body: "Your parents live in the house and you live abroad. Pair property care with [parent care visits](/p/nri-parent-care-mohali) — one team watches over both your home and the people in it." },
      ],
    },
    services_kicker: "What We Handle",
    services_title1: "Everything your home needs.",
    services_title2: "Nothing you must fly back for.",
    services_intro: "Whether your property in Chandigarh, Mohali or Panchkula is vacant, tenanted, or lived in by your parents — one team handles it all.",
    services: [
      "Monthly property inspection with a dated photo report on WhatsApp",
      "Repair & maintenance coordination with trusted local vendors",
      "Tenant verification, rent follow-up and agreement renewal support",
      "Utility bills, property tax and society dues payment assistance",
      "Deep cleaning, airing and pre-arrival preparation of your home",
      "Coordination with legal/documentation professionals when needed",
    ],
    steps_kicker: "How It Works",
    steps_title1: "Manage your Indian property",
    steps_title2: "from anywhere in the world.",
    steps: [
      { num: "1", title: "Tell us about your property", body: "Address, condition, tenanted or vacant — a free consultation call, wherever in the world you are." },
      { num: "2", title: "We visit and document everything", body: "Our Tricity team does a full first inspection with photos and a written condition report." },
      { num: "3", title: "Monthly reports, zero worry", body: "Every month: inspection, photos, payments coordinated, issues flagged before they grow. You simply read the report." },
    ],
    faq_kicker: "Common NRI Questions",
    faq_title1: "Asked by homeowners",
    faq_title2: "just like you.",
    faqs: [
      { q: "How do NRIs manage property in India from abroad?", a: "Most NRIs rely on relatives — which strains relationships and lacks accountability. A professional NRI property management service like ours gives you scheduled inspections, dated photo reports and a single point of contact, so your property in Chandigarh, Mohali or Panchkula is managed with documentation, not favours." },
      { q: "My house in Mohali is vacant. Is that a risk?", a: "Yes — vacant homes face encroachment risk, undetected damage and disconnection of utilities. Our monthly inspection with photographic proof keeps your home visibly occupied and maintained, with documented property-condition records shared after every visit." },
      { q: "Can you handle my tenants and rent?", a: "We verify tenants, support rent follow-ups, coordinate agreement renewals and conduct move-in/move-out inspections — keeping your rental income smooth without you flying down." },
    ],
    cta_title1: "Your home in the Tricity deserves",
    cta_title2: "a guardian, not a favour.",
    cta_subtext: "Book a free consultation. We'll visit your property, document everything, and show you exactly what monthly care looks like.",
    cta_button: "Protect My Property",
    cta_whatsapp_msg: "Hi! I own a property in the Tricity and want to know about your NRI property management service.",
  },
  parent_care_page: {
    kicker: "For NRI families",
    headline_line1: "NRI Parent Care in",
    headline_line2: "Chandigarh, Mohali & Panchkula",
    subtext: "Bi-weekly home visits, hospital appointment assistance and genuine companionship for your parents in the Tricity — with a photo report after every visit, so distance never feels like doubt.",
    stats: ["Bi-weekly home visits", "Report after every visit", "Background-verified team"],
    whatsapp_msg: "Sat Sri Akal! I'm an NRI and I'd like to talk about care visits for my parents in the Tricity.",
    image: "/images/hero-parents.jpg",
    image_quote: "They raised you with both hands. Let us be there with ours.",
    worries_kicker: "The Quiet Worries",
    worries_title1: "The 2 a.m. worry every NRI",
    worries_title2: "knows too well.",
    worries: [
      { title: "Appointments, alone", body: "Hospital queues, reports, follow-ups — aging parents often face them alone or postpone them entirely. We schedule appointments and accompany them, start to finish." },
      { title: "\"ਸਭ ਠੀਕ ਹੈ\" isn't always true", body: "Parents hide loneliness and small health changes to protect you from worry. Regular friendly visits notice what phone calls can't." },
      { title: "Small tasks become big hurdles", body: "A bank visit, a medicine pickup, a leaking tap, a phone that stopped working — little things pile up. We handle errands and coordinate fixes." },
      { title: "Emergencies need someone local", body: "When minutes matter, relatives are hours away and you are oceans away. Our Tricity team reaches them first and updates you immediately." },
    ],
    audience: {
      kicker: "Who Is This Service For?",
      title1: "Made for sons and daughters",
      title2: "living oceans away.",
      intro: "If your parents live in Chandigarh, Mohali or Panchkula and you live abroad, this service is your presence when you can't be there. Own a property too? Pair this with our [NRI property management](/nri-property-management), or see [everything we do](/).",
      items: [
        { title: "Independent but aging parents", body: "They manage daily life but shouldn't do everything alone. Bi-weekly visits bring company, help and a watchful, caring eye." },
        { title: "Parents with regular appointments", body: "Monthly check-ups, tests, specialist visits — we schedule, accompany, wait, and bring them home safely, then update you the same day." },
        { title: "Families who want proof, not promises", body: "Every visit ends with a dated photo report and honest notes on WhatsApp — mood, home condition, errands done. You'll feel the distance shrink." },
      ],
    },
    services_kicker: "What Every Visit Includes",
    services_title1: "Real presence.",
    services_title2: "Not just phone calls.",
    services_intro: "A familiar, background-verified face your parents look forward to — on a schedule, with documentation you can trust.",
    services: [
      "Bi-weekly or weekly home visits on a fixed schedule",
      "Chai, conversation and genuine companionship",
      "Hospital appointment scheduling and escort assistance",
      "Errands — medicines, bills, banking and small daily needs",
      "Festival and birthday check-ins on your behalf",
      "Dated photo report and honest notes after every visit",
    ],
    honest_note: "We are not a medical or nursing provider. We make sure your parents reach the right care, on time, and never alone.",
    steps_kicker: "How It Works",
    steps_title1: "From first call to first visit,",
    steps_title2: "in days — not months.",
    steps: [
      { num: "1", title: "Tell us about your parents", body: "A free video or WhatsApp call — their routine, appointments, likes and worries. We listen first." },
      { num: "2", title: "We meet them at home", body: "Our team visits, builds trust over chai, and designs a visit plan around their life — not a template." },
      { num: "3", title: "Visits begin, reports flow", body: "Scheduled visits with photo reports after every one. You'll know how they are — always." },
    ],
    faq_kicker: "Questions From NRI Children",
    faq_title1: "Asked by families",
    faq_title2: "just like yours.",
    faqs: [
      { q: "How can I take care of my parents in Mohali while living abroad?", a: "Tricity Family Concierge visits your parents on a fixed schedule, assists with hospital appointments, helps with errands and sends a photo report after every visit — so you can care for them from Canada, the UK, the US, Australia or anywhere else." },
      { q: "Is this a medical or nursing service?", a: "No — and we're honest about that. We are a companionship and assistance service. We schedule appointments, accompany your parents to the doctor, wait with them and bring them home safely. For medical care itself, we help you connect with the right professionals." },
      { q: "How do I know the visits really happen?", a: "Every visit ends with a dated photo report and written notes sent to your WhatsApp — their mood, the home's condition, errands completed and the next visit date. Complete Care plans include video updates too." },
    ],
    cta_title1: "Your parents deserve",
    cta_title2: "a familiar face, not a favour.",
    cta_subtext: "Book a free family consultation. We'll meet your parents, understand their routine, and show you exactly what regular visits look like.",
    cta_button: "Plan Their First Visit",
    cta_whatsapp_msg: "Sat Sri Akal! I'd like to plan care visits for my parents in the Tricity.",
  },
  business: {
    name: "Tricity Family Concierge",
    tagline: "Parents. Property. Peace of mind.",
    promise: "You live abroad. We stay close.",
    phone_display: "+91 98912 43309",
    whatsapp_number: "919891243309",
    email: "care@tricityfamilyconcierge.com",
    enquiry_email: "care@tricityfamilyconcierge.com",
    website_url: "https://www.tricityfamilyconcierge.com",
    service_areas: "Chandigarh · Mohali · Panchkula",
    description: "Parent care and property management for NRI families, delivered with the warmth of home. Not a medical service — a family that shows up.",
    instagram: "",
    facebook: "",
    linkedin: "",
    logo_url: "",
    favicon_url: "",
  },
  navigation: [
    { label: "Home", href: "/" },
    { label: "Parent Care", href: "/nri-parent-care" },
    { label: "Property Management", href: "/nri-property-management" },
    { label: "Services", href: "/services" },
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
  ],
  redirects: [],
  seo: {
    "/": {
      title: "Tricity Family Concierge | NRI Parent Care & Property Management in Chandigarh, Mohali & Panchkula",
      description: "NRI parent care and property management in Chandigarh, Mohali & Panchkula. Home visits, appointment assistance, property inspections, repairs, tenant coordination and photo reports.",
      canonical: "https://www.tricityfamilyconcierge.com/",
      noindex: false,
      og_image: "/images/hero-parents.jpg",
    },
    "/nri-parent-care": {
      title: "NRI Parent Care in Chandigarh, Mohali & Panchkula | Tricity Family Concierge",
      description: "Reliable NRI parent care in Chandigarh, Mohali & Panchkula. Home visits, companionship, appointment assistance, errands and dated WhatsApp reports for families living abroad.",
      canonical: "https://www.tricityfamilyconcierge.com/nri-parent-care",
      noindex: false,
      og_image: "/images/hero-parents.jpg",
    },
    "/nri-property-management": {
      title: "NRI Property Management in Chandigarh, Mohali & Panchkula | Tricity Family Concierge",
      description: "NRI property management in Chandigarh, Mohali & Panchkula. Monthly inspections, vacant home checks, tenant coordination, repairs, bills and dated photo reports for NRIs.",
      canonical: "https://www.tricityfamilyconcierge.com/nri-property-management",
      noindex: false,
      og_image: "",
    },
    "/services": {
      title: "NRI Parent Care & Property Management Services | Tricity Family Concierge",
      description: "Explore our two service pillars: NRI parent care and NRI property management across Chandigarh, Mohali & Panchkula — visits, inspections, repairs, tenant coordination and photo reports.",
      canonical: "https://www.tricityfamilyconcierge.com/services",
      noindex: false,
      og_image: "",
    },
    "/about": {
      title: "About Tricity Family Concierge | Local Tricity Team for NRI Families",
      description: "A local Chandigarh Tricity team helping NRI families with parent care visits and property management — dependable, documented and personal.",
      canonical: "https://www.tricityfamilyconcierge.com/about",
      noindex: false,
      og_image: "",
    },
    "/contact": {
      title: "Contact Tricity Family Concierge | NRI Parent Care & Property Management",
      description: "Looking for NRI parent care or property management in Chandigarh, Mohali or Panchkula? Tell us what you need and we'll get back to you.",
      canonical: "https://www.tricityfamilyconcierge.com/contact",
      noindex: false,
      og_image: "",
    },
  },
  homepage_sections: [
    { id: "promise", enabled: true },
    { id: "serve", enabled: true },
    { id: "services", enabled: true },
    { id: "how", enabled: true },
    { id: "packages", enabled: true },
    { id: "trust", enabled: true },
    { id: "about_home", enabled: true },
    { id: "enquiry", enabled: true },
    { id: "faq", enabled: true },
  ],
  homepage_serve: {
    kicker: "Who We Serve",
    title1: "Built for families",
    title2: "living overseas.",
    body: "Tricity Family Concierge is built for families living overseas who have parents, homes or both in Chandigarh, Mohali and Panchkula. Whether your parents need regular local support, your house is vacant, your property has tenants, or you simply need someone trustworthy on the ground — we're here in the Tricity.",
    countries_note: "We regularly support families in Canada, the USA, the UK, Australia, the UAE and other countries.",
  },
  homepage_about: {
    kicker: "About Tricity Family Concierge",
    title1: "A local Tricity team for families",
    title2: "who live far away.",
    body: "Tricity Family Concierge was created with a simple idea: distance should never mean losing touch with what matters at home. We provide dependable on-ground assistance for NRI families with parents and properties across Chandigarh, Mohali and Panchkula. From a scheduled visit to your parents, to checking an empty home, coordinating a repair or preparing your property before your India visit — we become your trusted local point of contact.",
    cta_label: "More About Us",
    cta_href: "/about",
  },
  services_page: {
    kicker: "Our Services",
    h1: "NRI Parent Care & Property Management Services",
    intro: "Two pillars. One trusted local team in the Tricity. Choose what your family needs — or combine both for complete peace of mind.",
    pillar1: {
      title: "NRI Parent Care",
      body: "Bi-weekly home visits, hospital appointment assistance, companionship, errands and dated photo reports — so your parents in Chandigarh, Mohali or Panchkula are never alone and you are never guessing.",
      cta_label: "Explore Parent Care",
      cta_href: "/nri-parent-care",
    },
    pillar2: {
      title: "NRI Property Management",
      body: "Scheduled inspections of vacant and tenanted homes, repair and maintenance coordination, tenant support, utility assistance and dated photo reports — your property, cared for like family.",
      cta_label: "Explore Property Management",
      cta_href: "/nri-property-management",
    },
  },
  about_page: {
    kicker: "Our Story",
    h1: "About Tricity Family Concierge",
    intro: "A local Tricity team for families who live far away.",
    blocks: [
      { heading: "What we do", text: "Tricity Family Concierge provides two dedicated services for NRI families: parent care — scheduled home visits, hospital appointment assistance, companionship and errands — and property management for vacant, tenanted and parent-occupied homes across Chandigarh, Mohali and Panchkula." },
      { heading: "Why we exist", text: "Distance should never mean losing touch with what matters at home. We started this service after seeing how hard it is for families abroad to be present for aging parents and empty homes — and how much a trustworthy local team changes that." },
      { heading: "Who we serve", text: "NRI families living in Canada, the USA, the UK, Australia, the UAE and beyond — anyone with parents, a home, or both in the Tricity who wants dependable, documented, personal support." },
      { heading: "How we work", text: "Trust through documentation: scheduled visits, dated photo reports, honest written notes and WhatsApp updates after every visit. One local point of contact. No lock-in contracts. We are not a medical or nursing provider — and we say so plainly." },
    ],
  },
  contact_page: {
    kicker: "Contact",
    h1: "Contact Tricity Family Concierge",
    intro: "Looking for NRI parent care or property management in Chandigarh, Mohali or Panchkula? Tell us what you need and we'll get back to you.",
  },
};
